import { useNavigate, useLocation } from "react-router-dom";
import { Typography, Link, Fade } from '@mui/material';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/system';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useState } from 'react';

// Import icons
import HomeFilledIcon from '@mui/icons-material/HomeFilled';
import LiveHelpIcon   from '@mui/icons-material/LiveHelp';
import EmailIcon      from '@mui/icons-material/Email';
import PersonIcon     from '@mui/icons-material/Person';
import LogoutIcon     from '@mui/icons-material/Logout';
import GroupsIcon     from '@mui/icons-material/Groups';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MenuIcon       from '@mui/icons-material/Menu';

// Local imports
import { useAuth } from '../AuthContext';
import { useAssignedVideos } from '../AssignedVideosContext';
import { api } from '../../api';

const activeStyles = {
    transition: '0.3s',
    backgroundColor: 'var(--northwestern-purple-10)',
    borderRadius: '5px',
    '@media (max-width:1100px)': {
        borderRadius: 0
    }
}

const linkStyles = {
    textDecoration: 'none',
    color: 'var(--northwestern-purple)',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    padding: {
        sm: '0.3rem',
        md: '0.7rem',
    },
    '&:hover': activeStyles,
}

const navTextStyles = {
    fontSize: 'clamp(12px, 5vw, 15px)',
    whiteSpace: 'nowrap',
    '@media (max-width:1100px)': {
        fontSize: '10px'
    }
}

const listStyles = {
    display: 'flex',
    justifyContent: 'space-around',
    listStyle: 'none',
    flex: 2,
    '@media (max-width:1100px)': {
        padding: 0
    }
}

const navTitleStyles = {
    flex: 1,
    color: 'var(--northwestern-purple)',
    fontWeight: {
        sm: '500',
        md: '700',
    },
    textDecoration: 'none',
    fontSize: {
        sm: '0.8rem',
        md: '1.2rem',
    }
}

const navStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #c4c4c4',
    paddingLeft: {
        xs: '1rem',
        sm: '0.3rem',
        md: '1.5rem',
    },
    paddingRight: {
        xs: '0.5rem',
        sm: '0.5rem',
        md: '1.5rem',
    },
    paddingTop: 0,
}

const userMenuStyles = {
    display: 'flex',
    justifyContent: 'flex-end',
    borderBottom: '1px solid #c4c4c4',
    paddingRight: '1rem',
    paddingLeft: '1rem',
    '@media (max-width:1100px)': {
        marginTop: 0,
        paddingRight: '0.5rem',
        paddingLeft: '0.5rem',
    }
}

const userMenuItemStyles = {
    paddingRight: '0.5rem',
    margin: '0.5rem',
    color: '#716C6B',
    textDecoration: 'none',
    fontSize: '1rem',
    '&:hover': {
        color: 'var(--northwestern-purple)',
        transition: '0.3s',
    },
    '@media (max-width:1100px)': {
        margin: '0.2rem',
        fontSize: '0.8rem',
    }
}

const userMenuActiveStyles = {
    color: 'var(--northwestern-purple)',
}

const iconStyles = {
    fontSize: {
        xs: '18px',
        sm: '16px',
        md: '20px'
    },
    mr: '0.5rem',
}

const menuLinkStyles = {
    textDecoration: 'none',
    fontSize: '0.8rem',
    color: 'var(--northwestern-purple)',
    '&:hover': activeStyles
}

const NavBar = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const location = useLocation();
    const currentPath = location.pathname;
    
    const { isLoggedIn, setIsLoggedIn, loading, isAdmin } = useAuth(); // Using AuthContext to check if user is authenticated
    const { videosCount } = useAssignedVideos(); // Using AssignedVideosContext to check if user has assigned videos
    const navigate = useNavigate();
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const navLinks = Object.freeze({
        home: { 
            href: '/', 
            label: t('navbar.home') 
        },
        dashboard: { 
            href: '/dashboard', 
            label: 'Dashboard'
        },
        newParticipant: { 
            href: '/new-participant', 
            label: 'New Participant'
        },
        surveyResponses: {
            href: '/survey/responses',
            label: 'Survey Responses'
        },
        portal: { 
            href: '/portal', 
            label: t('navbar.portal') 
        },
        vaccineInfo: { 
            href: '/vaccine-info', 
            label: t('navbar.vaccine_info') 
        },
        videos: { 
            href: '/videos', 
            label: videosCount === 1 ? t('navbar.my_video') : t('navbar.my_videos') 
        },
        team: {
            href: '/team',
            label: t('navbar.team')
        },
        participate: {
            href: '/participate',
            label: t('navbar.participate')
        },
        faq: {
            href: '/faq',
            label: t('navbar.faq')
        },
        contact: {
            href: '/contact',
            label: t('navbar.contact')
        }
    })

    const handleClick = (e) => {
        setAnchorEl(e.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    // Log out user
    const logout = async () => {
        navigate('/', {
                    state: {
                        severity: 'success',
                        message: t('navbar.logout_alert'),
                    },
                    replace: true
                });
        try {
            await api.get('/auth/logout');
            setIsLoggedIn(false);
        } catch (err) {
            console.error(err);
        }
    }

    return (
        <Fade in timeout={1000}>
            <div>
                <Box component="nav">
                    {!loading && (
                        <List sx={userMenuStyles}>
                        {isLoggedIn && (
                            isAdmin ? (
                                <>
                                    <Link href={navLinks.dashboard.href} sx={{...userMenuItemStyles, ...(currentPath === navLinks.dashboard.href ? userMenuActiveStyles : {})}}>{navLinks.dashboard.label}</Link>
                                    <Link href={navLinks.surveyResponses.href} sx={{...userMenuItemStyles, ...(currentPath === navLinks.surveyResponses.href ? userMenuActiveStyles : {})}}>{navLinks.surveyResponses.label}</Link>
                                    <Link href={navLinks.newParticipant.href} sx={{...userMenuItemStyles, ...(currentPath === navLinks.newParticipant.href ? userMenuActiveStyles : {})}}>{navLinks.newParticipant.label}</Link>
                                </>
                            ) : (
                            <>
                                <Link href={navLinks.portal.href} sx={{...userMenuItemStyles, ...(currentPath === navLinks.portal.href ? userMenuActiveStyles : {})}}>{navLinks.portal.label}</Link>
                                <Link href={navLinks.vaccineInfo.href} sx={{...userMenuItemStyles, ...(currentPath === navLinks.vaccineInfo.href ? userMenuActiveStyles : {})}}>{navLinks.vaccineInfo.label}</Link>
                                {videosCount > 0 && (
                                    <Link href={navLinks.videos.href} sx={{...userMenuItemStyles, ...(currentPath === navLinks.videos.href ? userMenuActiveStyles : {})}}>
                                        {navLinks.videos.label}
                                    </Link>
                                )}
                            </>
                            )
                        )}

                        {isLoggedIn ? (
                            <Link component="button" onClick={logout} sx={userMenuItemStyles}><LogoutIcon style={{ fontSize: "small", verticalAlign: "middle", marginRight: '0.2rem' }}></LogoutIcon>{t('navbar.logout')}</Link>
                        ) : (
                            <Link href="/login" sx={userMenuItemStyles}><PersonIcon style={{ fontSize: "large", verticalAlign: "middle", marginRight: '0.5rem' }}></PersonIcon>{t('navbar.login')}</Link> 
                        )}
                        </List>
                    )}
                </Box>
                <Box component="nav" sx={navStyles}>
                    <Box><Link href="/" sx={navTitleStyles}>{t('navbar.title')}</Link></Box>
                    {isMobile ? (
                        <div>
                            <Button
                                id="menu-button"
                                aria-controls={open ? 'menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                                onClick={handleClick}
                            >
                                <MenuIcon sx={{ color: 'var(--northwestern-purple)'}} />
                            </Button>
                            <Menu
                                id="menu"
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                dense={true}
                                slotProps={{
                                    list: {
                                        'aria-labelledby': 'menu',
                                    },
                                }}
                            >
                                <MenuItem
                                    component="a"
                                    href={navLinks.home.href}
                                    onClick={handleClose}
                                    sx={{...menuLinkStyles, ...(currentPath === navLinks.home.href ? activeStyles : {})}}
                                >
                                    <HomeFilledIcon sx={iconStyles} />
                                    {navLinks.home.label}
                                </MenuItem>
                                <MenuItem
                                    component="a"
                                    href={navLinks.team.href}
                                    onClick={handleClose}
                                    sx={{...menuLinkStyles, ...(currentPath === navLinks.team.href ? activeStyles : {})}}
                                >
                                    <GroupsIcon sx={iconStyles} />
                                    {navLinks.team.label}
                                </MenuItem>
                                <MenuItem
                                    component="a"
                                    href={navLinks.participate.href}
                                    onClick={handleClose}
                                    sx={{...menuLinkStyles, ...(currentPath === navLinks.participate.href ? activeStyles : {})}}
                                >
                                    <AssignmentIcon sx={iconStyles} />
                                    {navLinks.participate.label}
                                </MenuItem>
                                <MenuItem
                                    component="a"
                                    href={navLinks.faq.href}
                                    onClick={handleClose}
                                    sx={{...menuLinkStyles, ...(currentPath === navLinks.faq.href ? activeStyles : {})}}
                                >
                                    <LiveHelpIcon sx={iconStyles} />
                                    {navLinks.faq.label}
                                </MenuItem>
                                <MenuItem
                                    component="a"
                                    href={navLinks.contact.href}
                                    onClick={handleClose}
                                    sx={{...menuLinkStyles, ...(currentPath === navLinks.contact.href ? activeStyles : {})}}
                                >
                                    <EmailIcon sx={iconStyles} />
                                    {navLinks.contact.label}
                                </MenuItem>
                            </Menu>
                        </div>
                    ) : (
                        <List sx={listStyles}>
                            <Link href={navLinks.home.href} sx={{...linkStyles, ...(currentPath === navLinks.home.href ? activeStyles : {})}}>
                                <HomeFilledIcon sx={iconStyles} />
                                <Typography variant="h6" sx={navTextStyles}>{navLinks.home.label}</Typography>
                            </Link>
                            <Link href={navLinks.team.href} sx={{...linkStyles, ...(currentPath === navLinks.team.href ? activeStyles : {})}}>
                                <GroupsIcon sx={iconStyles} />
                                <Typography variant="h6" sx={navTextStyles}>{navLinks.team.label}</Typography>
                            </Link>
                            <Link href={navLinks.participate.href} sx={{...linkStyles, ...(currentPath === navLinks.participate.href ? activeStyles : {})}}>
                                <AssignmentIcon sx={iconStyles} />
                                <Typography variant="h6" sx={navTextStyles}>{navLinks.participate.label}</Typography>
                            </Link>
                            <Link href={navLinks.faq.href} sx={{...linkStyles, ...(currentPath === navLinks.faq.href ? activeStyles : {})}}>
                                <LiveHelpIcon sx={iconStyles} />
                                <Typography variant="h6" sx={navTextStyles}>{navLinks.faq.label}</Typography>
                            </Link>
                            <Link href={navLinks.contact.href} sx={{...linkStyles, ...(currentPath === navLinks.contact.href ? activeStyles : {})}}>
                                <EmailIcon sx={iconStyles} />
                                <Typography variant="h6" sx={navTextStyles}>{navLinks.contact.label}</Typography>
                            </Link>
                        </List>
                    )}
                </Box>
            </div>
        </Fade>
    )
}

export default NavBar;